import { Fragment, memo, useCallback, useEffect, useMemo, useContext } from 'react'
import Divider from '@mui/material/Divider'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../store'
import { Box, Button, Stack, Typography } from '@mui/material'
import AlertAlarm from '../../../assets/icons/AlertAlarm'
import { AppContext } from '../../../App'
import { selectActiveChartId } from '../../../selectors/chart'
import { UserAlert, UserAlert_Type } from '../../../proto/generated/nexus'
import ROUTES from '../../../assets/routes'
import { useLocation, useNavigate } from 'react-router-dom'
import { SECURITY_URL_PARAM, ViewType } from '../../../assets/constants'
import useSelectView from '../../../hooks/useSelectView'
import AlertListItem from '../alertListItem/alertListItem'
import { selectAlertsLoaded } from '../../../selectors/alerts'
import { setAlertModalData } from '../../../reducer/alerts.reducer'
import { createUserAlertFromSnapshot, getFocusedAlertableSourceDrawing } from '../../../util/alerts'
import { fetchUserAlerts } from '../../../services/apis/alerts'
import { useStyles } from './styles'
import { useUser } from '../../../context/UserContext'
import { FeatureCategory, FeatureSubCategory } from '../../adminUserRestrictions/Restrictions.constants'
import { Group, setFeatureRestrictionDialog } from '../../../reducer/user.reducer'
import { FeatureCode } from '../../../util/RestrictionFeatureCode'
import SubtleShimmer from '../../common/SubtleShimmer'
import AlertsListSkeleton from '../AlertsListSkeleton'

function AlertList() {
    const dispatch = useDispatch()
    const userAlerts = useSelector((state: RootState) => state.alertsStore.alerts)
    const alertsLoaded = useSelector(selectAlertsLoaded)
    const activeChartId = useSelector(selectActiveChartId)
    const appContext = useContext(AppContext)
    const navigate = useNavigate()
    const location = useLocation()
    const view = useSelectView()
    const {user: {hasAccess}} = useUser()
    const showOpenChart: boolean = useMemo(() => view !== ViewType.CHART, [view, location.pathname])
    const { classes } = useStyles()

    useEffect(() => {
        if (alertsLoaded) return
        fetchUserAlerts({ dispatch }).catch(() => {})
    }, [alertsLoaded, dispatch])

    const openChartView = useCallback(() => {
        const queryParams = new URLSearchParams(location.search)
        const mainSecurity = queryParams.get(SECURITY_URL_PARAM)
        let finalRoute = ROUTES.base
        if (mainSecurity) {
            finalRoute += `?${SECURITY_URL_PARAM}=${mainSecurity}`
        }
        navigate(finalRoute)
    }, [location.search, navigate])

    const onCreateAlertClicked = useCallback(() => {
        if(!hasAccess(FeatureCategory.CHART, FeatureSubCategory.ALERTS)) {
            dispatch(setFeatureRestrictionDialog({
                open: true,
                feature: "Alerts",
                featureRestrictionCode:FeatureCode.CHART_ALERTS,
                group: Group.CHART
            }))
            return
        }
        
        const chartSnapshotMethod = appContext.getChartSnapshot[activeChartId]
        const snapshot = chartSnapshotMethod ? chartSnapshotMethod() : null

        if (!snapshot) return
        const chart = appContext.getChart(activeChartId)
        const sourceDrawing = chart ? getFocusedAlertableSourceDrawing(chart) : undefined
        dispatch(setAlertModalData({
            chartSnapshot: snapshot,
            alert: createUserAlertFromSnapshot(snapshot),
            edit: false,
            sourceDrawing,
        }))
    }, [activeChartId, appContext, dispatch, hasAccess])

    const emptyListView = useMemo(() => {
        return (
            <Box className={classes.emptyAlertContainer}>
                <AlertAlarm />
                <Stack direction={'column'} alignItems={'center'}>
                    <Typography variant={'body2'} component={'span'} align={'center'}>
                        {'Alerts notify you instantly when your conditions are met. '}
                    </Typography>
                    <Typography variant={'body2'} component={'span'}>
                        {showOpenChart ? 'To set one up, please open the chart first.' : 'Create one to get started.'}
                    </Typography>
                </Stack>
                <Button variant={'contained'} color={'inherit'} onClick={showOpenChart ? openChartView : onCreateAlertClicked}>
                    {showOpenChart ? 'Open chart' : 'Add alert'}
                </Button>
            </Box>
        )
    }, [showOpenChart, openChartView, onCreateAlertClicked, classes])

    const loadingView = useMemo(() => (
        <SubtleShimmer loading fill>
            <AlertsListSkeleton />
        </SubtleShimmer>
    ), [])

    const alertsView = useMemo(() => {
        return (
            <>
                {userAlerts
                    .filter((userAlert?: UserAlert) => Boolean(userAlert))
                    .map((userAlert: UserAlert) => {
                        if (userAlert.type === UserAlert_Type.SCREENER) return null
                        return (
                            <Fragment key={userAlert.id}>
                                <AlertListItem alert={userAlert} />
                                <Divider />
                            </Fragment>
                        )
                    })}
            </>
        )
    }, [userAlerts])

    const content = useMemo(() => {
        if (!alertsLoaded) return loadingView
        return userAlerts.length === 0 ? emptyListView : alertsView
    }, [loadingView, alertsView, emptyListView, alertsLoaded, userAlerts.length])

    return content
}

export default memo(AlertList)
